import React from 'react';
import {Link, Typography, List, ListItem} from '@mui/material';
import {formatDate, parseToJson} from '..';
import {
  radioTicketVisibilityOptions,
  TicketVisibilityEnums,
} from '../voucher/VoucherUtils';
import {StringIsNullOrEmptyOrUndefined} from '../helpers';
import {Box} from '@mui/system';
import {NavLink} from 'react-router-dom';
import {path} from '../../routes/path';
import colors from '../../theme/colors';

export const ProgressEnums = {
  IN_PROGRESS: 1,
  COMPLETED: 2,
  FAILED: 3,
};

export const StatusColorEnums = {
  SUCCESS: '#00918D',
  IN_PROGRESS: '#B72126',
  FAILED: '#B72126',
};
export const renderDistributionLogsDateTime = (dateTime: string) => {
  const formatDateString = 'DD/MM/YYYY';
  const formatTimeString = 'HH:mm';
  return (
    <>
      <Typography variant="body2" fontSize="13px" fontWeight="bold">
        {formatDate(dateTime, formatDateString)}
      </Typography>
      {formatDate(dateTime, formatTimeString)}
    </>
  );
};

export const renderEmail = (row: any = {}, defaultValue = '--') => {
  try {
    const {sfmcDefinitionInstanceID, sfmcJourneyID, sfmcJourneyVersion} = row;
    return (
      <>
        <Typography variant="body2" fontSize="13px">
          {row?.email ?? defaultValue}
        </Typography>
        {(sfmcDefinitionInstanceID || sfmcJourneyID || sfmcJourneyVersion) && (
          <Typography fontSize="10px" fontStyle="italic">
            via <b>SFMC</b>
          </Typography>
        )}
      </>
    );
  } catch (e) {
    return defaultValue;
  }
};

export const renderDistributionLogsDetails = (
  row: any = {},
  fallbackValue: any = '-',
  config: any = {}
) => {
  try {
    const renderSfmcReferenceItem = (label: string, value?: string) => {
      return (
        ![null, undefined].includes(value) && (
          <ListItem
            sx={{
              display: 'list-item',
              p: 0,
              listStyleType: 'disc',
            }}
          >
            <Typography component="span" fontSize="10px" fontStyle="italic">
              {label}:{' '}
            </Typography>
            <Typography
              component="span"
              fontSize="10px"
              fontStyle="italic"
              fontWeight="bolder"
            >
              {value}
            </Typography>
          </ListItem>
        )
      );
    };

    const {
      targetedTicketNumberId,
      targetedTicketTitle,
      targetedTicketExcerpt,
      targetedTicketId,
      targetedMembershipQty = 0,
      action = '{}',
      sfmcDefinitionInstanceID,
      sfmcJourneyID,
      sfmcJourneyVersion,
      sfmcJourneyActivityID,
    } = row;

    const {
      renderSendExclusiveBannerOnB2CPlatforms = () => {},
      renderSendPushNotificationToOptedInMembers = () => {},
    } = config;

    const parsedAction = parseToJson(action, {});
    const {
      membershipType: {membershipIds = [], membershipCSVs = []} = {},
      quantity,
    } = parsedAction;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Box>
          Voucher selected:{' '}
          <Typography fontWeight="bolder">
            <Link
              underline="hover"
              color={colors.systemLink}
              component={NavLink}
              to={`/${path.VOUCHER}/${targetedTicketId}`}
            >
              [{targetedTicketNumberId}] {targetedTicketTitle} |{' '}
              {targetedTicketExcerpt}
            </Link>
          </Typography>
        </Box>
        <Box>
          <Typography component="span" fontWeight="bolder">
            {targetedMembershipQty}
          </Typography>{' '}
          targeted member(s):{' '}
          <Typography component="span" fontWeight="bolder">
            {membershipIds.length === 1 ? (
              membershipIds[0]
            ) : membershipCSVs.length > 0 ? (
              <Link
                underline="hover"
                color={colors.systemLink}
                href={membershipCSVs[0]}
                target="_blank"
              >
                UploadedMembershipID.csv
              </Link>
            ) : (
              '-'
            )}
          </Typography>
        </Box>
        <Box>
          Quantity per member:{' '}
          <Typography component="span" fontWeight="bolder">
            {quantity}
          </Typography>
        </Box>
        <Box>
          Distributed into member’s pocket:{' '}
          <Typography component="span" fontWeight="bolder">
            {{true: 'Yes', false: 'No'}?.[parsedAction?.addToPocket] ?? '-'}
          </Typography>
        </Box>
        <Box>{renderSendExclusiveBannerOnB2CPlatforms(parsedAction)}</Box>
        <Box>{renderSendPushNotificationToOptedInMembers(parsedAction)}</Box>
        {(sfmcDefinitionInstanceID ||
          sfmcJourneyID ||
          ![null, undefined].includes(sfmcJourneyVersion) ||
          sfmcJourneyActivityID) && (
          <Box>
            <Typography fontSize="10px" fontStyle="italic">
              SFMC references:
            </Typography>
            <List
              sx={{
                listStyle: 'disc',
                p: 0,
                pl: 2,
                ml: 2,
              }}
            >
              {renderSfmcReferenceItem(
                'Definition Instance ID',
                sfmcDefinitionInstanceID
              )}
              {renderSfmcReferenceItem('Journey ID', sfmcJourneyID)}
              {renderSfmcReferenceItem('Version', sfmcJourneyVersion)}
              {renderSfmcReferenceItem(
                'Journey Activity ID',
                sfmcJourneyActivityID
              )}
            </List>
          </Box>
        )}
      </Box>
    );
  } catch (e) {
    return fallbackValue;
  }
};

export const renderDistributionLogsResults = (row: any) => {
  const actionObject = JSON.parse(row?.action ?? '{}');
  const ticketTargetedDistribution = row?.ticketTargetedDistribution ?? false;
  const addToPocket = actionObject?.addToPocket ?? false;
  const pushNotification = actionObject?.pushNotification ?? false;
  const showExclusiveB2CBanner = actionObject?.showExclusiveB2CBanner ?? false;
  const parsedResult = parseToJson(row?.result ?? '{}', {});
  const membershipCount = parsedResult?.successfulMembershipIdCount ?? 0;
  const quantity = parsedResult?.DistributedIntoPocketQty ?? 0;

  const visibilityString =
    ticketTargetedDistribution === true
      ? radioTicketVisibilityOptions[
          TicketVisibilityEnums.TARGETED
        ].key.toLowerCase()
      : radioTicketVisibilityOptions[
          TicketVisibilityEnums.PUBLIC
        ].key.toLowerCase();
  let label = '';
  let desc = '';
  // render desc
  const descPrefix = addToPocket === true ? 'including' : 'with';
  if (pushNotification === true && showExclusiveB2CBanner === true) {
    desc = `${descPrefix} notifications and an exclusive banner on B2C platforms`;
  } else if (pushNotification === true && showExclusiveB2CBanner === false) {
    desc = `${descPrefix} notifications`;
  } else if (pushNotification === false && showExclusiveB2CBanner === true) {
    desc = `${descPrefix} an exclusive banner on B2C platforms`;
  }

  // render title
  if (addToPocket === true) {
    label = `${quantity} ${visibilityString} voucher(s) directly to ${membershipCount} member(s)' pocket(s)`;
  } else {
    label = `Notified ${membershipCount} member(s) of a ${visibilityString} voucher`;
  }

  return (
    <>
      <Typography variant="body2" fontSize="13px" fontWeight="bold">
        {label}
      </Typography>
      {desc}
    </>
  );
};

export const renderDistributionLogsStatus = (row: any) => {
  let statusLabel = '--';
  let statusDesc = '';
  let color = '';
  const isUnsuccessfulCSV = StringIsNullOrEmptyOrUndefined(
    row?.unsuccessfulCsv
  );
  switch (row.progressId) {
    case ProgressEnums.IN_PROGRESS:
      statusLabel = 'In progress';
      statusDesc = '';
      color = StatusColorEnums.IN_PROGRESS;
      break;
    case ProgressEnums.COMPLETED:
      statusLabel = 'Completed';
      statusDesc = isUnsuccessfulCSV ? '' : 'With unsuccessful details';
      color = StatusColorEnums.SUCCESS;
      break;
    case ProgressEnums.FAILED:
      statusLabel = 'Failed';
      statusDesc = '';
      color = StatusColorEnums.FAILED;
      break;
    default:
      statusLabel = '--';
      statusDesc = '';
      color = '';
      break;
  }
  return (
    <>
      <Typography
        color={color}
        variant="body2"
        fontSize="13px"
        fontWeight="bold"
      >
        {statusLabel}
      </Typography>
      {statusDesc}
    </>
  );
};
